import React, { useEffect, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftCircle } from 'react-feather';
import tippy from 'tippy.js';
import 'tippy.js/dist/tippy.css';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import api from '../network/ApiClient';

const ALL_MERCHANTS = 'all';

const CalendarView = () => {
  const [rides, setRides] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date().toISOString().slice(0, 7));
  const { routeId } = useParams();

  // Merchant filter. GET /organizergroup is already scoped to the caller's
  // memberships; we only show the dropdown when they belong to more than one
  // merchant. "All" omits the `merchant` param — see RIDES_API.md (the
  // `merchant` query param narrows within the caller's scope).
  const [merchants, setMerchants] = useState([]);
  const [selectedMerchant, setSelectedMerchant] = useState(ALL_MERCHANTS);

  // Passenger-manifest modal.
  const [manifestOpen, setManifestOpen] = useState(false);
  const [manifestTitle, setManifestTitle] = useState('');
  const [manifestRows, setManifestRows] = useState([]);
  const [manifestLoading, setManifestLoading] = useState(false);

  const fetchRides = async (month, merchant) => {
    try {
      const params = new URLSearchParams();
      if (routeId) params.append('route', routeId);
      if (month) params.append('month', month);
      // Omit the param for "All"; otherwise narrow to the chosen merchant.
      if (merchant && merchant !== ALL_MERCHANTS) params.append('merchant', merchant);

      const res = await api.get(`/ride?${params.toString()}`);
      if (res.data) {
        setRides(
          res.data.map((ride) => ({
            id: ride.id,
            title: `${ride.routename} - ${new Date(ride.depart).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            })} (${ride.bookings})`,
            start: ride.depart.split(' ')[0],
            allDay: true,
            drivername: ride.drivername,
            vehiclename: ride.vehiclename,
            bookings: ride.bookings,
            merchantname: ride.merchantname,
            departTime: `${new Date(ride.depart).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            })}`,
          }))
        );
      }
    } catch (error) {
      console.error('Error fetching rides:', error);
    }
  };

  const loadMerchants = async () => {
    try {
      const res = await api.get('organizergroup');
      setMerchants(res.data?.organizer || []);
    } catch (error) {
      console.error('Unable to load merchants', error);
    }
  };

  useEffect(() => {
    loadMerchants();
  }, []);

  useEffect(() => {
    fetchRides(currentMonth, selectedMerchant);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMonth, selectedMerchant]);

  const handleDateChange = (date) => {
    const monthDate = new Date(date);
    monthDate.setMonth(monthDate.getMonth() + 1);
    setCurrentMonth(monthDate.toISOString().slice(0, 7));
  };

  const openManifest = async (rideId, title) => {
    setManifestTitle(title);
    setManifestRows([]);
    setManifestOpen(true);
    setManifestLoading(true);
    try {
      const res = await api.get(`/ride/${rideId}/manifest`);
      setManifestRows(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Unable to load passenger manifest', error);
    } finally {
      setManifestLoading(false);
    }
  };

  const handleEventClick = (info) => {
    // No bookings ⇒ no manifest to show (RIDES_API.md).
    if (!info.event.extendedProps.bookings) return;
    openManifest(info.event.id, info.event.title);
  };

  const handleEventDidMount = (info) => {
    const event = info.event.extendedProps;
    tippy(info.el, {
      content: `<div>Driver: ${event.drivername} <br/> Vehicle: ${event.vehiclename} <br/> Departure Time: ${event.departTime}</div>`,
      allowHTML: true,
      theme: 'light',
    });
  };

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '10px',
          marginBottom: '10px',
        }}
      >
        <Link
          to={!routeId ? '/routes' : `/rides/${routeId}`}
          style={{
            width: '6rem',
            height: '2.5rem',
            padding: '10px',
            color: 'white',
            backgroundColor: 'black',
            borderRadius: '5px',
            textDecoration: 'none',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          <ArrowLeftCircle /> Back
        </Link>

        {merchants.length > 1 ? (
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="merchant-filter-label">Merchant</InputLabel>
            <Select
              labelId="merchant-filter-label"
              label="Merchant"
              value={selectedMerchant}
              onChange={(e) => setSelectedMerchant(e.target.value)}
            >
              <MenuItem value={ALL_MERCHANTS}>All</MenuItem>
              {merchants.map((m) => (
                <MenuItem key={m.id} value={m.id}>
                  {m.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : null}
      </div>

      <FullCalendar
        dayPopoverFormat={{ month: 'long', day: 'numeric' }}
        plugins={[timeGridPlugin, dayGridPlugin, interactionPlugin]}
        initialView="dayGridMonth"
        events={rides}
        headerToolbar={{
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth',
        }}
        editable={false}
        datesSet={(dateInfo) => handleDateChange(dateInfo.start)}
        eventClick={handleEventClick}
        eventDidMount={handleEventDidMount}
        eventContent={(eventInfo) => (
          <div style={{ whiteSpace: 'normal', overflow: 'visible', width: '100%', cursor: 'pointer' }}>
            <b>{eventInfo.event.title}</b>
          </div>
        )}
      />

      <Dialog open={manifestOpen} onClose={() => setManifestOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Passenger Manifest{manifestTitle ? ` — ${manifestTitle}` : ''}</DialogTitle>
        <DialogContent dividers>
          {manifestLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
              <CircularProgress />
            </div>
          ) : manifestRows.length === 0 ? (
            <Typography>No passengers booked on this ride.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>First Name</TableCell>
                  <TableCell>Last Name</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Boarded At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {manifestRows.map((p) => (
                  <TableRow key={p.code}>
                    <TableCell>{p.firstname}</TableCell>
                    <TableCell>{p.lastname}</TableCell>
                    <TableCell>
                      {/* Linked card: phone is already the parent's; flag it with
                          the parent's first name in brackets (RIDES_API.md). */}
                      {p.linked_card ? `${p.phone} (${p.parent_firstname})` : p.phone}
                    </TableCell>
                    <TableCell>{p.boarded_at || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManifestOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CalendarView;
